import { useEffect, useState } from 'react'
import { ExternalLink, Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  TASK_PRIORITY_LABELS,
  TASK_STATUS_LABELS,
  type PagyMeta,
  type TaskRow,
  type TaskStatus,
} from '@/types/work'

// "Tâches" tab embedded in donor + opportunity sheets (PR 10 / Unit 11).
// Fetches /work/tasks.json scoped to the subject and renders a compact
// task list + "Nouvelle tâche" CTA that deep-links to /work/tasks/new
// with the subject prefilled and `source=sheet` so SC1 (≥70% of tasks
// created from a sheet) can be measured server-side.
//
// Lives in the host-app layer (app/frontend/components/work) so neither
// crm_people nor crm_fundraising imports anything from crm_work — keeps
// the engine tier rules clean (ADR-ARC-002 / CLAUDE.md §Engine
// Architecture). Engines just call <TasksSlot subjectType=… subjectId=… />.

interface TasksSlotProps {
  subjectType: string
  subjectId: number
  // Closed subjects (archived donor, lost opportunity) still show the
  // tab so existing tasks remain visible, but the create CTA is hidden
  // — there's no useful "+ Nouvelle tâche" affordance once the subject
  // is itself terminal.
  isSubjectClosed?: boolean
}

interface TasksResponse {
  tasks: TaskRow[]
  pagy: PagyMeta
  filters: unknown
}

const STATUS_BADGE: Record<TaskStatus, 'default' | 'destructive' | 'secondary' | 'outline'> = {
  pending: 'secondary',
  in_progress: 'default',
  on_hold: 'outline',
  completed: 'outline',
  cancelled: 'destructive',
  archived: 'outline',
}

function formatDue(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('fr-FR')
}

function isOverdue(task: TaskRow): boolean {
  if (!task.due_at) return false
  if (['completed', 'cancelled', 'archived'].includes(task.status)) return false
  return new Date(task.due_at).getTime() < Date.now()
}

export function TasksSlot({ subjectType, subjectId, isSubjectClosed = false }: TasksSlotProps) {
  const [page, setPage] = useState(1)
  const [data, setData] = useState<TasksResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Manual reload counter so retry-on-error re-runs the effect even when
  // none of the param-derived deps changed.
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    setData(null)
    setPage(1)
  }, [subjectType, subjectId])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)

    const url = `/work/tasks.json?subject_type=${encodeURIComponent(subjectType)}` +
      `&subject_id=${subjectId}&page=${page}`

    fetch(url, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
      credentials: 'same-origin',
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json() as Promise<TasksResponse>
      })
      .then(setData)
      .catch((err) => {
        if (err.name === 'AbortError') return
        setError('Erreur de chargement des tâches.')
      })
      .finally(() => setLoading(false))

    return () => controller.abort()
  }, [subjectType, subjectId, page, reloadKey])

  const newTaskHref =
    `/work/tasks/new?subject_type=${encodeURIComponent(subjectType)}` +
    `&subject_id=${subjectId}&source=sheet`

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {loading && !data && 'Chargement…'}
          {data && `${data.pagy.count} tâche${data.pagy.count !== 1 ? 's' : ''}`}
          {error && '—'}
        </p>
        {!isSubjectClosed && (
          <Button asChild size="sm">
            <a href={newTaskHref}>
              <Plus className="mr-1.5 h-4 w-4" />
              Nouvelle tâche
            </a>
          </Button>
        )}
      </div>

      {error ? (
        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <p className="text-destructive">{error}</p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() => setReloadKey((k) => k + 1)}
          >
            Réessayer
          </Button>
        </div>
      ) : loading && !data ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : data && data.tasks.length === 0 ? (
        <p className="rounded-md border bg-muted/30 p-4 text-center text-sm text-muted-foreground">
          Aucune tâche liée pour le moment.
        </p>
      ) : data ? (
        <>
          <ul className="divide-y rounded-md border bg-card">
            {data.tasks.map((t) => (
              <TaskRowItem key={t.id} task={t} />
            ))}
          </ul>
          {data.pagy.pages > 1 && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                Page {data.pagy.page} / {data.pagy.pages}
              </span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Précédent
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={page >= data.pagy.pages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  )
}

function TaskRowItem({ task }: { task: TaskRow }) {
  const overdue = isOverdue(task)
  return (
    <li
      className={cn(
        'flex items-start justify-between gap-3 px-3 py-2',
        ['cancelled', 'archived'].includes(task.status) && 'text-muted-foreground'
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm">
          <Badge variant={STATUS_BADGE[task.status]} className="text-xs">
            {TASK_STATUS_LABELS[task.status]}
          </Badge>
          <Badge variant="outline" className="text-xs">
            {TASK_PRIORITY_LABELS[task.priority]}
          </Badge>
          <span className={cn('truncate font-medium', overdue && 'text-destructive')}>
            {task.title}
          </span>
        </div>
        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
          <span>Échéance : {formatDue(task.due_at)}</span>
          {task.assignee_name && (
            <>
              <span>·</span>
              <span>{task.assignee_name}</span>
            </>
          )}
          {task.subtask_count > 0 && (
            <>
              <span>·</span>
              <span>{task.subtask_count} sous-tâche{task.subtask_count > 1 ? 's' : ''}</span>
            </>
          )}
        </div>
      </div>
      <a
        href={`/work/tasks/${task.id}`}
        className="shrink-0 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
        title="Voir la tâche"
      >
        Voir
        <ExternalLink className="h-3 w-3" />
      </a>
    </li>
  )
}
